import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { ButtonType } from 'components/Button/Button';
import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import LoanBreakdownCard from 'components/LoanBreakdownCard';
import DiscountsExpander from 'components/DiscountsExpander/DiscountsExpander';

import { ConfirmLoanResult } from 'enums/FlowNextResults';

import { FlowComponentType } from 'routes/types';

import { getApplicationData as getApplicationDataSelector } from 'selectors/getApplicationData';
import { getCardData } from 'selectors/getCardData';
import { getApplicationData, getApplicationApr, updateApplicationStatus } from 'thunks';

import { ConversionType, trackConversion } from 'utils/analytics';
import { RoutePath } from 'enums/Routes';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';

import styles from './ConfirmLoan.module.scss';

const ConfirmLoan = ({ navigationInfo, handleNext }: FlowComponentType): JSX.Element => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const { application, isApplicationStatusUpdating, isLoading: isApplicationLoading, apr, isAprLoading } = useSelector(
    getApplicationDataSelector,
  );
  const isFinalizeReviewOfferScreen = pathname === RoutePath.FinalizeReviewOffer;

  const { applied: cardApplied } = useSelector(getCardData);

  const getCredentialString = () =>
    application?.borrowerCredentials && application?.borrowerCredentials !== 'None'
      ? application?.borrowerCredentials
      : '';

  useEffect(() => {
    const refreshApplication = async () => {
      if (application) {
        dispatchWithUnwrap(getApplicationData(application.id));
        dispatchWithUnwrap(getApplicationApr(application.id));
      }
    };

    refreshApplication();
  }, []);

  useEffect(() => {
    if (application && !apr && !isAprLoading) {
      dispatchWithUnwrap(getApplicationApr(application.id));
    }
  }, [application]);

  const getTitle = () => {
    if (isFinalizeReviewOfferScreen) {
      return `Review your updated Personal ${getCredentialString()} Plan`;
    }
    return `Your Personal ${getCredentialString()} Plan`;
  };

  const handleContinue = async () => {
    trackConversion(ConversionType.ConfirmedLoan);
    if (application?.status === ApplicationStatusName.OfferAvailable) {
      await dispatchWithUnwrap(
        updateApplicationStatus({
          applicationId: application!.id,
          applicationStatus: ApplicationStatusName.Verification,
        }),
      );
    }
    handleNext(ConfirmLoanResult.Continue);
  };

  return (
    <>
      <FormNavigation
        {...navigationInfo}
        {...(isFinalizeReviewOfferScreen && { onBackClick: () => navigate(RoutePath.SelectDebts) })}
      />
      <FormContainer title={getTitle()}>
        <DiscountsExpander />

        <LoanBreakdownCard />

        <div className={styles.buttonContainer}>
          <Button
            onClick={handleContinue}
            isLoading={isApplicationStatusUpdating || isApplicationLoading || !application}
          >
            Next
          </Button>

          {cardApplied && (
            <Button
              type={ButtonType.Inverted}
              onClick={() => {
                handleNext(ConfirmLoanResult.BackToFinancialDashboard);
              }}
            >
              Not Now
            </Button>
          )}
          {isFinalizeReviewOfferScreen && (
            <Button type={ButtonType.Transparent} onClick={() => navigate(RoutePath.SelectDebts)}>
              Make a change
            </Button>
          )}
        </div>
      </FormContainer>
    </>
  );
};

export default ConfirmLoan;
